import { DataType, Form, FormOptions, Section, SelectOption } from '@globaleris/ui.library'
import { makeStyles, useTheme } from '@material-ui/core'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumClaseFactura } from '../../../../api'
import { enumToSelectOptionArray } from '../../../../helpers'
import { getCustomerDomain } from '../../../../redux'
import { styles } from '../../invoicesPage.styles'

interface InvoiceFormProps {
  invoiceFormData?: InvoiceFormModel
  editMode?: boolean
  onFormChange: (formState: InvoiceFormModel, formIsCompleted: boolean) => void
  productsSelectOptions?: SelectOption[]
}

export interface InvoiceFormModel {
  id?: number
  code: string
  subject?: string
  nifSubject?: string
  subjectSource?: string
  nifSubjectSource?: string
  class?: EnumClaseFactura
  contractCode?: string
  cups?: string
  productIds?: number[]
  startDate?: Date
  endDate?: Date
}

interface InvoiceFormValidation {
  code: boolean
  allComplete: boolean
}

const initialFormValidation = {
  code: false,
  allComplete: false
}

export const InvoiceForm: React.FC<InvoiceFormProps> = ({
  invoiceFormData,
  editMode = false,
  onFormChange,
  productsSelectOptions = []
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'invoicesPage'])

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const [formData, setFormData] = useState<InvoiceFormModel>(
    invoiceFormData ?? ({} as InvoiceFormModel)
  )
  const [formValidations, setFormValidations] = useState<InvoiceFormValidation>({
    ...initialFormValidation,
    code: !!invoiceFormData?.code,
    allComplete: invoiceFormData ? Object.keys(invoiceFormData).length > 0 : false
  })

  const handleFormIsEnabled = (key: keyof InvoiceFormValidation, value: boolean): boolean => {
    const formIsCompleted = Object.keys(initialFormValidation)
      .filter(k => k !== 'allComplete')
      .every(k => (k === key ? value : formValidations[k as keyof InvoiceFormValidation]))

    setFormValidations(prevState => ({
      ...prevState,
      [key]: value,
      allComplete: formIsCompleted
    }))

    return formIsCompleted
  }

  const options: FormOptions = {
    elevation: 2,
    columns: 3,
    analytics: {
      customer: getCustomerDomain(),
      view: t('invoicesPage:forms.add.analytics')
    }
  }

  const sections: Section[] = [
    {
      title: t('invoicesPage:forms.add.title'),
      inputs: [
        {
          name: 'code',
          label: t('invoicesPage:forms.add.inputs.code'),
          inputSize: 3,
          type: DataType.TEXT,
          required: true,
          onChange: (value, formState) => {
            const isComplete = !!value && value !== ''
            const formIsCompleted = handleFormIsEnabled('code', isComplete)
            const castFormState = formState as InvoiceFormModel
            onFormChange(castFormState, formIsCompleted)
          },
          readOnly: editMode
        },
        {
          name: 'class',
          label: t('invoicesPage:forms.add.inputs.class'),
          inputSize: 3,
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumClaseFactura, 'enums:EnumClaseFactura'),
          readOnly: editMode
        },
        {
          name: 'startDate',
          label: t('invoicesPage:forms.add.inputs.startDate'),
          inputSize: 3,
          type: DataType.DATE,
          readOnly: editMode
        },
        {
          name: 'endDate',
          label: t('invoicesPage:forms.add.inputs.endDate'),
          inputSize: 3,
          type: DataType.DATE,
          readOnly: editMode
        },
        {
          name: 'subject',
          label: t('invoicesPage:forms.add.inputs.subject'),
          inputSize: 3,
          type: DataType.TEXT,
          readOnly: editMode
        },
        {
          name: 'nifSubject',
          label: t('invoicesPage:forms.add.inputs.nifSubject'),
          inputSize: 3,
          type: DataType.TEXT,
          readOnly: editMode
        },
        {
          name: 'subjectSource',
          label: t('invoicesPage:forms.add.inputs.subjectSource'),
          inputSize: 3,
          type: DataType.TEXT,
          readOnly: editMode
        },
        {
          name: 'nifSubjectSource',
          label: t('invoicesPage:forms.add.inputs.nifSubjectSource'),
          inputSize: 3,
          type: DataType.TEXT,
          readOnly: editMode
        },
        {
          name: 'contractCode',
          label: t('invoicesPage:forms.add.inputs.contractCode'),
          inputSize: 4,
          type: DataType.TEXT,
          readOnly: editMode
        },
        {
          name: 'cups',
          label: t('invoicesPage:forms.add.inputs.cups'),
          inputSize: 4,
          type: DataType.TEXT,
          readOnly: editMode
        },
        {
          name: 'productIds',
          label: t('invoicesPage:forms.add.inputs.productIds'),
          inputSize: 4,
          type: DataType.MULTISELECT,
          selectOptions: productsSelectOptions,
          readOnly: editMode
        }
      ]
    }
  ]

  return (
    <div className={`${classes.noHeader} ${classes.form}`}>
      <Form title={''} sections={sections} data={invoiceFormData} options={options} />
    </div>
  )
}
